import { CadastroEmpresaProgress } from "@/components/CadastroEmpresaProgress";
import { ClientLogo } from "@/components/ClientLogo";
import { EmpresaParceiraFormFields } from "@/components/EmpresaParceiraFormFields";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useClientConfig } from "@/contexts/ClientConfigContext";
import { api } from "@/lib/api";
import { getClientPrimaryButtonStyle } from "@/lib/clientPrimaryButton";
import {
  EMPTY_EMPRESA_PARCEIRA_FORM,
  validateEmpresaParceiraForm,
  validateEmpresaParceiraStep1,
  validateEmpresaParceiraStep2,
  empresaParceiraFormToPayload,
  clubeEmpresasDisplayName,
} from "@/lib/empresaParceiraFields";
import { toast } from "@/lib/appToast";
import { Loader2 } from "lucide-react";
import { useState, type FormEvent, type KeyboardEvent } from "react";

type Step = 1 | 2 | 3;

export default function CadastroEmpresaPage() {
  const { config, loading: configLoading } = useClientConfig();
  const [form, setForm] = useState(EMPTY_EMPRESA_PARCEIRA_FORM);
  const [step, setStep] = useState<Step>(1);
  const [maxReached, setMaxReached] = useState<Step>(1);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const primaryStyle = getClientPrimaryButtonStyle(config.primary_color);

  const clubeNome = clubeEmpresasDisplayName(config);
  const pageTitle = configLoading
    ? "Cadastro Clube Empresas"
    : clubeNome
      ? `Cadastro Clube ${clubeNome} Empresas`
      : "Cadastro Clube Empresas";

  const handleAdvance = () => {
    if (step === 1) {
      const err = validateEmpresaParceiraStep1(form);
      if (err) {
        toast.error(err);
        return;
      }
      setStep(2);
      setMaxReached((m) => (m < 2 ? 2 : m));
      return;
    }
    if (step === 2) {
      const err = validateEmpresaParceiraStep2(form);
      if (err) {
        toast.error(err);
        return;
      }
      setStep(3);
      setMaxReached(3);
    }
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const handleStepClick = (target: Step) => {
    if (target <= maxReached) setStep(target);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (step !== 3 || saving) return;

    const err = validateEmpresaParceiraForm(form, { quantidadeOpcional: true });
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      await api.cadastrarEmpresaParceiraPublica(empresaParceiraFormToPayload(form));
      setSuccess(true);
      setForm(EMPTY_EMPRESA_PARCEIRA_FORM);
      setStep(1);
      setMaxReached(1);
      toast.success("Cadastro realizado com sucesso!");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao enviar cadastro");
    } finally {
      setSaving(false);
    }
  };

  const preventEarlySubmit = (e: KeyboardEvent) => {
    if (step !== 3 && e.key === "Enter") {
      const tag = (e.target as HTMLElement).tagName;
      if (tag !== "TEXTAREA") e.preventDefault();
    }
  };

  const stepVariant = step === 1 ? "step1" : step === 2 ? "step2" : "step3";
  const FormWrapper = step === 3 ? "form" : "div";
  const wrapperProps =
    step === 3
      ? { onSubmit: (e: FormEvent) => void handleSubmit(e) }
      : { onKeyDown: preventEarlySubmit };

  return (
    <div className="min-h-screen bg-[#F4F6F8] px-4 py-8">
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6">
        <ClientLogo variant="menu" className="h-20 w-auto max-w-[320px] object-contain" />
        <Card className="w-full border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-center text-xl font-semibold text-gray-900">
              {pageTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {success ? (
              <p className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                Cadastro enviado com sucesso. Obrigado pelo interesse em fazer parte do{" "}
                {clubeNome ? `Clube ${clubeNome} Empresas` : "programa de empresas parceiras"}.
              </p>
            ) : null}
            <FormWrapper {...wrapperProps} className="space-y-4">
              <CadastroEmpresaProgress
                step={step}
                maxReached={maxReached}
                primaryColor={config.primary_color}
                secondaryColor={config.secondary_color}
                onStepClick={handleStepClick}
              />
              <EmpresaParceiraFormFields
                form={form}
                onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                idPrefix="public"
                variant={stepVariant}
              />
              <div
                className={
                  step === 1
                    ? "flex pt-2"
                    : "flex gap-2 pt-2"
                }
              >
                {step > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={saving}
                    onClick={handleBack}
                    className="min-w-0 flex-1 border-gray-200"
                  >
                    Voltar
                  </Button>
                ) : null}
                {step < 3 ? (
                  <Button
                    type="button"
                    disabled={saving}
                    style={primaryStyle}
                    className={`hover:opacity-90 ${step === 1 ? "w-full" : "min-w-0 flex-1"}`}
                    onClick={handleAdvance}
                  >
                    Avançar
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={saving}
                    style={primaryStyle}
                    className="min-w-0 flex-1 hover:opacity-90"
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Enviar
                  </Button>
                )}
              </div>
            </FormWrapper>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
